import { useEffect, useMemo, useState } from "react";
import api, { API, formatErr } from "@/lib/api";
import { Download, Loader2, RefreshCw, FileDown, AlertCircle, Archive, Trash2 } from "lucide-react";

const STATUS_LABELS = {
  bounced: "Bounced",
  unsub: "Unsubscribed",
  complained: "Complained",
};

function tokenParam() {
  const t = localStorage.getItem("rahi_token") || "";
  return `token=${encodeURIComponent(t)}`;
}

function buildCsvUrl({ group, status }) {
  const qs = new URLSearchParams();
  if (group) qs.set("group", group);
  if (status) qs.set("status", status);
  const s = qs.toString();
  return `${API}/reports/suppressions.csv${s ? "?" + s : ""}`;
}

async function downloadCsv({ group, status }) {
  // Use axios so the Bearer token from interceptor is attached, then blob-download.
  const params = {};
  if (group) params.group = group;
  if (status) params.status = status;
  const res = await api.get("/reports/suppressions.csv", {
    params,
    responseType: "blob",
  });
  const parts = ["suppressions"];
  if (group) parts.push(group);
  if (status) parts.push(status);
  const filename = parts.join("_") + ".csv";
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [group, setGroup] = useState("");
  const [status, setStatus] = useState("");
  const [downloadingKey, setDownloadingKey] = useState("");
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/suppressions/summary");
      setSummary(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    if (!summary?.by_group) return [];
    return Object.entries(summary.by_group)
      .map(([g, counts]) => ({ group: g, ...counts }))
      .sort((a, b) => b.total_suppressed - a.total_suppressed);
  }, [summary]);

  const totals = useMemo(() => {
    if (summary?.totals) return summary.totals;
    // Fallback (older backend without unique totals): sum per-group counts
    const t = { bounced: 0, unsub: 0, complained: 0, total_suppressed: 0 };
    rows.forEach((r) => {
      t.bounced += r.bounced || 0;
      t.unsub += r.unsub || 0;
      t.complained += r.complained || 0;
      t.total_suppressed += r.total_suppressed || 0;
    });
    return t;
  }, [rows, summary]);

  const groups = useMemo(() => rows.map((r) => r.group).filter((g) => g && g !== "(no group)"), [rows]);

  const runDownload = async (opts, key) => {
    setDownloadingKey(key);
    try {
      await downloadCsv(opts);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setDownloadingKey("");
    }
  };

  const runZipDownload = async () => {
    setDownloadingKey("zip");
    try {
      const res = await api.get("/reports/suppressions.zip", { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `suppressions_bundle_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setDownloadingKey("");
    }
  };

  const runPurge = async () => {
    if (purgeConfirm !== "PURGE SUPPRESSED") return;
    setPurging(true);
    setPurgeResult(null);
    setError("");
    try {
      const { data } = await api.post("/recipients/purge-suppressed", { confirm: "PURGE SUPPRESSED" });
      setPurgeResult(data);
      setPurgeConfirm("");
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight">
            Suppression <span style={{ color: "var(--rahi-red)" }}>reports</span>
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--rahi-soft)" }}>
            Bounces, unsubscribes and complaints — grouped by tag. Download a slice to send to the team responsible for cleaning that database.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runZipDownload}
            disabled={downloadingKey === "zip" || (summary?.totals?.total_suppressed || 0) === 0}
            className="btn btn-primary"
            data-testid="reports-zip-btn"
            title="Bundle every group + status CSV into one ZIP for partner handoff"
          >
            {downloadingKey === "zip" ? <Loader2 className="animate-spin" size={14} /> : <Archive size={14} />}
            Download ZIP bundle
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="btn btn-ghost"
            data-testid="reports-refresh-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-md border flex gap-3 items-start" style={{ borderColor: "#F3B4A5", background: "#FFF4F0" }} data-testid="reports-error">
          <AlertCircle size={16} className="mt-0.5" style={{ color: "var(--rahi-red)" }} />
          <div className="text-sm" style={{ color: "#8A2A16" }}>{error}</div>
        </div>
      )}

      {/* Totals card */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total suppressed", value: totals.total_suppressed, testid: "stat-total" },
          { label: "Bounced", value: totals.bounced, testid: "stat-bounced" },
          { label: "Unsubscribed", value: totals.unsub, testid: "stat-unsub" },
          { label: "Complained", value: totals.complained, testid: "stat-complained" },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border p-5" style={{ borderColor: "var(--rahi-border)", background: "var(--rahi-surface)" }} data-testid={c.testid}>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--rahi-soft)" }}>{c.label}</div>
            <div className="font-display text-3xl font-black tabular-nums">{c.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Custom download panel */}
      <div className="rounded-lg border mb-8 p-6" style={{ borderColor: "var(--rahi-border)", background: "var(--rahi-surface)" }}>
        <div className="flex items-center gap-2 mb-4">
          <FileDown size={16} style={{ color: "var(--rahi-red)" }} />
          <h2 className="font-display text-lg font-bold">Custom download</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider block mb-2" style={{ color: "var(--rahi-soft)" }}>Group</label>
            <select
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className="w-full px-3 py-2 rounded-md border text-sm bg-white"
              style={{ borderColor: "var(--rahi-border)" }}
              data-testid="reports-group-select"
            >
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider block mb-2" style={{ color: "var(--rahi-soft)" }}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 rounded-md border text-sm bg-white"
              style={{ borderColor: "var(--rahi-border)" }}
              data-testid="reports-status-select"
            >
              <option value="">All statuses</option>
              <option value="bounced">Bounced only</option>
              <option value="unsub">Unsubscribed only</option>
              <option value="complained">Complained only</option>
            </select>
          </div>
          <button
            onClick={() => runDownload({ group, status }, "custom")}
            disabled={downloadingKey === "custom"}
            className="btn btn-primary"
            data-testid="reports-custom-download-btn"
          >
            {downloadingKey === "custom" ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
            Download CSV
          </button>
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--rahi-soft)" }}>
          Filename adjusts automatically — e.g. <code>suppressions_staff_bounced.csv</code>. Leave a filter blank to include everything for that dimension.
        </p>
      </div>

      {/* Per-group breakdown table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--rahi-border)", background: "var(--rahi-surface)" }}>
        <div className="p-5 border-b" style={{ borderColor: "var(--rahi-border)" }}>
          <h2 className="font-display text-lg font-bold">Per-group breakdown</h2>
          <p className="text-xs mt-1" style={{ color: "var(--rahi-soft)" }}>
            One row per tag. Tap a status count to download just that slice.
          </p>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm flex items-center gap-2 justify-center" style={{ color: "var(--rahi-soft)" }}>
            <Loader2 className="animate-spin" size={14} /> Loading suppression counts…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--rahi-soft)" }}>
            No suppressed recipients yet. Once Mailgun webhooks fire, they'll show up here.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="reports-group-table">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--rahi-border)", background: "#FBF9F6" }}>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Group</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Bounced</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Unsub</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Complained</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Total</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Download</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const g = r.group;
                const isReal = g && g !== "(no group)";
                return (
                  <tr key={g} className="border-b hover:bg-[#FBF9F6]" style={{ borderColor: "var(--rahi-border)" }} data-testid={`reports-row-${g}`}>
                    <td className="px-5 py-3 font-medium">{g}</td>
                    {["bounced", "unsub", "complained"].map((s) => {
                      const n = r[s] || 0;
                      const key = `${g}::${s}`;
                      return (
                        <td key={s} className="px-5 py-3 text-right tabular-nums">
                          {n > 0 && isReal ? (
                            <button
                              onClick={() => runDownload({ group: g, status: s }, key)}
                              disabled={downloadingKey === key}
                              className="hover:underline"
                              style={{ color: "var(--rahi-red)" }}
                              data-testid={`reports-dl-${g}-${s}`}
                              title={`Download ${g} ${STATUS_LABELS[s]} as CSV`}
                            >
                              {downloadingKey === key ? "…" : n.toLocaleString()}
                            </button>
                          ) : (
                            <span style={{ color: n === 0 ? "var(--rahi-soft)" : "inherit" }}>{n.toLocaleString()}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{(r.total_suppressed || 0).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right">
                      {isReal && r.total_suppressed > 0 ? (
                        <button
                          onClick={() => runDownload({ group: g }, `${g}::all`)}
                          disabled={downloadingKey === `${g}::all`}
                          className="btn btn-ghost text-xs py-1"
                          data-testid={`reports-dl-${g}-all`}
                        >
                          {downloadingKey === `${g}::all` ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />}
                          Full CSV
                        </button>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--rahi-soft)" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2" style={{ borderColor: "var(--rahi-border)", background: "#FBF9F6" }}>
                <td className="px-5 py-3 font-bold">All groups</td>
                <td className="px-5 py-3 text-right tabular-nums font-bold">{totals.bounced.toLocaleString()}</td>
                <td className="px-5 py-3 text-right tabular-nums font-bold">{totals.unsub.toLocaleString()}</td>
                <td className="px-5 py-3 text-right tabular-nums font-bold">{totals.complained.toLocaleString()}</td>
                <td className="px-5 py-3 text-right tabular-nums font-bold">{totals.total_suppressed.toLocaleString()}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => runDownload({}, "all::all")}
                    disabled={downloadingKey === "all::all" || totals.total_suppressed === 0}
                    className="btn btn-primary text-xs py-1"
                    data-testid="reports-dl-all"
                  >
                    {downloadingKey === "all::all" ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />}
                    Everything
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Danger Zone — Purge suppressed */}
      <div className="rounded-lg border mt-8 p-6" style={{ borderColor: "#F3B4A5", background: "#FFF9F6" }} data-testid="reports-purge-card">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={16} style={{ color: "var(--rahi-red)" }} />
          <h2 className="font-display text-lg font-bold" style={{ color: "#8A2A16" }}>Purge suppressed recipients</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: "#8A2A16" }}>
          Permanently delete every recipient marked <strong>bounced</strong>, <strong>unsubscribed</strong>, or <strong>complained</strong> from the app database. Your remaining pool per group will be clean and ready for the next mailer.
          <br />
          <span className="text-xs" style={{ color: "var(--rahi-soft)" }}>Tip: download the ZIP bundle above first — that's your permanent record for the partner-team cleanup.</span>
        </p>
        <div className="flex gap-3 items-center">
          <input
            type="text"
            value={purgeConfirm}
            onChange={(e) => setPurgeConfirm(e.target.value)}
            placeholder='Type "PURGE SUPPRESSED" to enable'
            className="flex-1 px-3 py-2 rounded-md border text-sm bg-white"
            style={{ borderColor: "var(--rahi-border)" }}
            data-testid="reports-purge-confirm-input"
          />
          <button
            onClick={runPurge}
            disabled={purging || purgeConfirm !== "PURGE SUPPRESSED"}
            className="btn btn-primary"
            style={{ background: purgeConfirm === "PURGE SUPPRESSED" ? "var(--rahi-red)" : undefined }}
            data-testid="reports-purge-btn"
          >
            {purging ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
            Purge now
          </button>
        </div>

        {purgeResult && (
          <div className="mt-5 p-4 rounded-md border bg-white" style={{ borderColor: "var(--rahi-border)" }} data-testid="reports-purge-result">
            <div className="text-sm font-semibold mb-2">
              Deleted {purgeResult.deleted?.toLocaleString?.() || purgeResult.deleted} rows
              &nbsp;·&nbsp; {(purgeResult.before || 0).toLocaleString()} → {(purgeResult.after || 0).toLocaleString()} recipients remaining
            </div>
            {purgeResult.by_group && Object.keys(purgeResult.by_group).length > 0 && (
              <table className="w-full text-xs mt-2">
                <thead>
                  <tr className="text-left" style={{ color: "var(--rahi-soft)" }}>
                    <th className="py-1">Group</th>
                    <th className="py-1 text-right">Bounced</th>
                    <th className="py-1 text-right">Unsub</th>
                    <th className="py-1 text-right">Complained</th>
                    <th className="py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(purgeResult.by_group).map(([g, c]) => (
                    <tr key={g} className="border-t" style={{ borderColor: "var(--rahi-border)" }}>
                      <td className="py-1">{g}</td>
                      <td className="py-1 text-right tabular-nums">{(c.bounced || 0).toLocaleString()}</td>
                      <td className="py-1 text-right tabular-nums">{(c.unsub || 0).toLocaleString()}</td>
                      <td className="py-1 text-right tabular-nums">{(c.complained || 0).toLocaleString()}</td>
                      <td className="py-1 text-right tabular-nums font-semibold">{(c.total || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
